/** XeAgents.ai**/
import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mail, Clock, Users, Send, Shield, CheckCircle, Loader2, MessageSquare, Headphones, Sparkles, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const CONTACT_EMAIL = "connect@xenonlabs.ai";

/** Built per render so the validation messages follow the page language. */
function contactFormSchema(messages: { name: string; email: string; message: string }) {
  return z.object({
    name: z.string().min(2, messages.name),
    email: z.string().email(messages.email),
    company: z.string().optional(),
    message: z.string().min(10, messages.message),
  });
}

type ContactFormData = z.infer<ReturnType<typeof contactFormSchema>>;

interface TrustElementProps {
  icon: typeof Clock;
  text: string;
  testId: string;
}

const TrustElement = ({ icon: Icon, text, testId }: TrustElementProps) => (
  <div
    className="flex items-center gap-3.5 text-muted-foreground group/trust"
    data-testid={testId}
  >
    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/40 dark:to-purple-900/30 flex items-center justify-center flex-shrink-0 group-hover/trust:scale-110 transition-transform duration-300">
      <Icon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
    </div>
    <span className="text-sm font-medium">{text}</span>
  </div>
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.4, 0.25, 1],
    },
  },
};

export function ContactSection() {
  const shouldReduceMotion = useReducedMotion();
  const { toast } = useToast();
  const { t } = useTranslation("landing");
  const schema = useMemo(
    () =>
      contactFormSchema({
        name: t("contact.errName"),
        email: t("contact.errEmail"),
        message: t("contact.errMessage"),
      }),
    [t],
  );

  const form = useForm<ContactFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const response = await apiRequest("POST", "/api/contact", data);
      return response.json();
    },
    onSuccess: (data) => {
      form.reset();
      toast({
        title: t("contact.successTitle"),
        description: data.message || t("contact.successDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("contact.errorTitle"),
        description: error.message || t("contact.errorDescription"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    contactMutation.mutate(data);
  };

  return (
    <section
      id="contact"
      className="py-16 sm:py-20 md:py-28 lg:py-36 relative overflow-hidden"
      data-testid="section-contact"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-violet-50/30 to-background dark:from-background dark:via-violet-950/10 dark:to-background" />
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-violet-200/20 dark:bg-violet-800/10 rounded-full filter blur-[100px]" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-200/20 dark:bg-purple-800/10 rounded-full filter blur-[100px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={shouldReduceMotion ? {} : containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start"
        >
          {/* Left Side - Info (2/5) */}
          <motion.div
            variants={shouldReduceMotion ? {} : itemVariants}
            className="lg:col-span-2 space-y-8"
          >
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/40 dark:to-purple-900/30 text-violet-700 dark:text-violet-300 text-sm font-semibold">
                <Sparkles className="h-4 w-4" />
                {t("contact.badge")}
              </div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]"
                data-testid="heading-contact"
              >
                {t("contact.titleA")}
                <span className="bg-gradient-to-r from-violet-600 to-purple-600 dark:from-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
                  {t("contact.titleB")}
                </span>
              </h2>
              <p
                className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md"
                data-testid="text-contact-description"
              >
                {t("contact.body")}
              </p>
            </div>

            {/* Email Contact Card */}
            <motion.a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 dark:bg-white/5 border border-violet-200/50 dark:border-violet-800/30 backdrop-blur-sm group hover:border-violet-400/70 dark:hover:border-violet-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/10"
              whileHover={shouldReduceMotion ? {} : { x: 4 }}
              data-testid="link-contact-email"
            >
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30 group-hover:shadow-violet-500/50 transition-shadow duration-300">
                <Mail className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("contact.emailUs")}</p>
                <p className="text-base font-bold group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                  {CONTACT_EMAIL}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-violet-600 dark:group-hover:text-violet-400 group-hover:translate-x-1 transition-all duration-300" />
            </motion.a>

            {/* Trust Elements */}
            <div className="space-y-4 pt-2">
              <TrustElement
                icon={Clock}
                text={t("contact.trustResponse")}
                testId="trust-response-time"
              />
              <TrustElement
                icon={Users}
                text={t("contact.trustEnterprise")}
                testId="trust-customer-count"
              />
              <TrustElement
                icon={Headphones}
                text={t("contact.trustConsult")}
                testId="trust-free-consultation"
              />
              <TrustElement
                icon={CheckCircle}
                text={t("contact.trustNoCommit")}
                testId="trust-no-commitment"
              />
            </div>
          </motion.div>

          {/* Right Side - Form (3/5) */}
          <motion.div variants={shouldReduceMotion ? {} : itemVariants} className="lg:col-span-3">
            <Card className="p-6 sm:p-8 md:p-10 rounded-3xl shadow-2xl shadow-violet-500/5 border border-violet-200/30 dark:border-violet-800/20 bg-background/90 backdrop-blur-md" data-testid="card-contact-form">
              <div className="space-y-1.5 mb-8">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  <h3 className="text-lg font-bold">{t("contact.formTitle")}</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("contact.formSubtitle")}
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-5"
                  data-testid="form-contact"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold">{t("contact.name")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("contact.namePlaceholder")}
                              className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-violet-500 focus:ring-violet-500/20 transition-colors"
                              data-testid="input-name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold">{t("contact.email")}</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="you@company.com"
                              className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-violet-500 focus:ring-violet-500/20 transition-colors"
                              data-testid="input-email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold">{t("contact.company")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("contact.companyPlaceholder")}
                            className="h-12 rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-violet-500 focus:ring-violet-500/20 transition-colors"
                            data-testid="input-company"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold">{t("contact.message")}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t("contact.messagePlaceholder")}
                            className="min-h-[130px] resize-none rounded-xl bg-muted/40 border-muted-foreground/15 focus:border-violet-500 focus:ring-violet-500/20 transition-colors"
                            data-testid="input-message"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full h-13 text-base font-bold rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white border-0 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300"
                    disabled={contactMutation.isPending}
                    data-testid="button-submit-contact"
                  >
                    {contactMutation.isPending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {t("contact.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        {t("contact.send")}
                      </>
                    )}
                  </Button>

                  <p
                    className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 pt-1"
                    data-testid="text-privacy-notice"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    {t("contact.privacy")}
                  </p>
                </form>
              </Form>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default ContactSection;
